import angular from 'angular'

import { err2str, defaultTo } from '../utils/bits'
import { offClick, onClickPassive } from '../utils/on-click'
import { attempt, partial, toLower, camelCase } from 'lodash-es'

import envRaw from '../utils/env-raw'
import setCss from '../utils/set-css'
import genExposeName from '../utils/gen-expose-name'

angular
  .module('ggs')
  .decorator('ngClickDirective', ($delegate, ApplyAsync) => {
    const orig = $delegate[0]
    orig.compile = (_$elm, _$attr) => {
      return ($scope, $elm, $attr) => {
        const handler = ApplyAsync($scope, $attr.ngClick)
        const eventName = defaultTo($scope.$eval($attr.ngClickEvent), envRaw.clickEvent)

        onClickPassive($elm, eventName, handler)
        $scope.$on('$destroy', partial(offClick, $elm, eventName, handler))
      }
    }
    return $delegate
  })
  .decorator('$portalElement', ($delegate) => {
    const $galleryPortalElement = angular.element('<ggs-root ggs-gallery-portal="true"/>')
    setCss($galleryPortalElement, { minWidth: 0, minHeight: 0 })
    $delegate.append($galleryPortalElement)
    return $galleryPortalElement
  })
  .decorator('$exceptionHandler', ($delegate, $injector) => {
    return function exceptionHandler(err, cause) {
      if (window.Bugsnag) attempt(() => window.Bugsnag.notifyException(err))
      if (envRaw.devMode) attempt(() => $injector.get('notification').error(err2str(err)))
      return $delegate(err, cause)
    }
  })
  .decorator('$httpBackend', ($jsonpCallbacks, domh, addIdPrefix) => {
    return function httpBackend(method, rawUrl, _post, callback) {
      if (toLower(method) !== 'jsonp') {
        throw new Error('$httpBackend: only jsonp is supported!')
      }

      const path = $jsonpCallbacks.createCallback(rawUrl)
      const url = rawUrl.replace('JSON_CALLBACK', path)
      const script = domh.createScript(addIdPrefix(path)).attach()
      script.strictSrc(url)
      script.addEventListener('load', onScriptEvent)
      script.addEventListener('error', onScriptEvent)

      function onScriptEvent(_event) {
        script.removeEventListener('load', onScriptEvent)
        script.removeEventListener('error', onScriptEvent)
        script.detach()

        const data = $jsonpCallbacks.getResponse(path)
        const called = $jsonpCallbacks.wasCalled(path)
        $jsonpCallbacks.removeCallback(path)

        called
          ? callback(200, data, '', 'load', 'complete')
          : callback(404, false, '', 'error', 'complete')
      }
    }
  })
  .decorator('$jsonpCallbacks', ($log, env) => {
    const camelKey = camelCase(env.props.galleryKey)
    const exposeName = genExposeName(`ggsCallbacks_${camelKey}_:count:`)

    let counter = 0
    const path2response = {}

    window[exposeName] = generate

    return {
      wasCalled,
      getResponse,
      removeCallback,
      createCallback
    }

    function generate(id) {
      return callback.bind(null, calcPath(id))
    }

    function calcPath(id) {
      return `${exposeName}(${id})`
    }

    function callback(path, data) {
      const response = path2response[path]
      if (response) {
        response.data = data
        response.called = true
      } else {
        $log.warn(`$jsonpCallbacks: [path=${path}] is not found!`)
      }
    }

    function createCallback(_url) {
      const id = counter++
      const path = calcPath(id)
      const response = { data: null, called: false }
      path2response[path] = response
      return path
    }

    function wasCalled(path) {
      return path2response[path].called
    }

    function getResponse(path) {
      return path2response[path].data
    }

    function removeCallback(path) {
      delete path2response[path]
    }
  })
